import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  CreditCard,
  Search,
  Calendar,
  Filter,
  Loader2,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCcw,
  FileSpreadsheet
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/Table';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { formatDate, formatPrice } from '../../utils/format';
import { fetchWithAuth } from '../../hooks/useAuth';
import { PaymentStatus, PaymentMethod, PaymentsResponse } from '../../types/payments';

interface PaymentFilters {
  search?: string;
  status?: PaymentStatus;
  dateFrom?: string;
  dateTo?: string;
}

const PAYMENT_STATUSES = Object.values(PaymentStatus);

const paymentStatusColors: Record<PaymentStatus, string> = {
  [PaymentStatus.PENDING]: 'bg-yellow-100 text-yellow-800 ring-1 ring-yellow-200',
  [PaymentStatus.PROCESSING]: 'bg-blue-100 text-blue-800 ring-1 ring-blue-200',
  [PaymentStatus.COMPLETED]: 'bg-green-100 text-green-800 ring-1 ring-green-200',
  [PaymentStatus.FAILED]: 'bg-red-100 text-red-800 ring-1 ring-red-200',
  [PaymentStatus.REFUNDED]: 'bg-gray-100 text-gray-800 ring-1 ring-gray-200',
};

const paymentStatusIcons: Record<PaymentStatus, React.ReactNode> = {
  [PaymentStatus.PENDING]: <Clock className="w-4 h-4" />,
  [PaymentStatus.PROCESSING]: <RefreshCcw className="w-4 h-4" />,
  [PaymentStatus.COMPLETED]: <CheckCircle className="w-4 h-4" />,
  [PaymentStatus.FAILED]: <XCircle className="w-4 h-4" />,
  [PaymentStatus.REFUNDED]: <RefreshCcw className="w-4 h-4" />,
};

export function PaymentsPage() {
  const [filters, setFilters] = React.useState<PaymentFilters>({
    search: '',
    status: undefined,
    dateFrom: '',
    dateTo: '',
  });

  const { data: paymentsResponse, isLoading } = useQuery<PaymentsResponse>({
    queryKey: ['payments', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);

      const response = await fetchWithAuth(`/api/admin/payments?${params}`);
      if (!response.ok) throw new Error('Eroare la încărcarea plăților');
      return response.json();
    },
  });

  // Calculate payment statistics
  const stats = React.useMemo(() => {
    if (!paymentsResponse?.payments) return {
      totalAmount: 0,
      completedAmount: 0,
      pendingAmount: 0,
      failedAmount: 0
    };

    return paymentsResponse.payments.reduce((acc, payment) => {
      acc.totalAmount += payment.amount;
      if (payment.status === PaymentStatus.COMPLETED) {
        acc.completedAmount += payment.amount;
      } else if (payment.status === PaymentStatus.PENDING) {
        acc.pendingAmount += payment.amount;
      } else if (payment.status === PaymentStatus.FAILED) {
        acc.failedAmount += payment.amount;
      }
      return acc;
    }, {
      totalAmount: 0,
      completedAmount: 0,
      pendingAmount: 0,
      failedAmount: 0
    });
  }, [paymentsResponse?.payments]);

  return (
    <div className="p-6 space-y-8 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow-sm">
        <div className="flex items-center gap-2">
          <CreditCard className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Plăți</h1>
            <p className="text-sm text-gray-500">
              Gestionează și monitorizează plățile
            </p>
          </div>
        </div>
        <Button 
          className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
          onClick={async () => {
            try {
              const response = await fetchWithAuth('/api/admin/payments/export', {
                method: 'GET',
                headers: {
                  'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                }
              });
              if (!response.ok) {
                throw new Error('Eroare la exportul plăților');
              }
              const blob = await response.blob();
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `plati-${new Date().toISOString().split('T')[0]}.xlsx`;
              document.body.appendChild(a);
              a.click();
              window.URL.revokeObjectURL(url);
              document.body.removeChild(a);
            } catch (error) {
              console.error('Export error:', error);
            }
          }}
        >
          <FileSpreadsheet className="w-4 h-4" />
          Export Excel
        </Button>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-500">
              <DollarSign className="w-4 h-4" />
              Total Plăți
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              {formatPrice(stats.totalAmount)}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-500">
              <CheckCircle className="w-4 h-4" />
              Plăți Finalizate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              {formatPrice(stats.completedAmount)}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-500">
              <Clock className="w-4 h-4" />
              Plăți în Așteptare
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              {formatPrice(stats.pendingAmount)}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-500">
              <XCircle className="w-4 h-4" />
              Plăți Eșuate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              {formatPrice(stats.failedAmount)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-white shadow-sm p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Search className="w-4 h-4" />
              Caută
            </label>
            <Input
              placeholder="Caută după ID, client..."
              value={filters.search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                setFilters(f => ({ ...f, search: e.target.value }))
              }
              className="mt-1"
            />
          </div>
          
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Filter className="w-4 h-4" />
              Status
            </label>
            <Select
              value={filters.status ?? 'ALL'}
              onValueChange={(value) => setFilters(f => ({ ...f, status: value === 'ALL' ? undefined : value as PaymentStatus }))}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Toate statusurile" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Toate statusurile</SelectItem>
                {PAYMENT_STATUSES.map(status => (
                  <SelectItem key={status} value={status}>
                    <div className="flex items-center gap-2">
                      {paymentStatusIcons[status]}
                      {status}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              De la
            </label>
            <Input
              type="date"
              value={filters.dateFrom}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                setFilters(f => ({ ...f, dateFrom: e.target.value }))
              }
              className="mt-1"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Până la
            </label>
            <Input
              type="date"
              value={filters.dateTo}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                setFilters(f => ({ ...f, dateTo: e.target.value }))
              }
              className="mt-1"
            />
          </div>
        </div>
      </Card>

      {/* Payments Table */}
      <Card className="bg-white shadow-sm">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-semibold">ID</TableHead>
                <TableHead className="font-semibold">Comandă</TableHead>
                <TableHead className="font-semibold">Client</TableHead>
                <TableHead className="font-semibold">Metodă</TableHead>
                <TableHead className="font-semibold">Sumă</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold">Data</TableHead>
                <TableHead className="font-semibold">Stripe ID</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-10">
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                      <p className="text-gray-500">Se încarcă...</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : !paymentsResponse || !paymentsResponse.payments || paymentsResponse.payments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-10">
                    <div className="text-gray-500">Nu există plăți</div>
                  </TableCell>
                </TableRow>
              ) : (
                paymentsResponse.payments.map((payment) => (
                  <TableRow key={payment.id} className="hover:bg-gray-50">
                    <TableCell className="font-medium">#{payment.id}</TableCell>
                    <TableCell className="font-medium">#{payment.orderId}</TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900">{payment.order.user.name}</span>
                        <span className="text-sm text-gray-500">{payment.order.user.email}</span>
                      </div>
                    </TableCell>
                    <TableCell>{payment.method}</TableCell>
                    <TableCell className="font-medium">{formatPrice(payment.amount)}</TableCell>
                    <TableCell>
                      <Badge className={paymentStatusColors[payment.status]}>
                        <div className="flex items-center gap-1">
                          {paymentStatusIcons[payment.status]}
                          {payment.status}
                        </div>
                      </Badge>
                    </TableCell>
                    <TableCell>{formatDate(payment.createdAt)}</TableCell>
                    <TableCell>
                      <span className="font-mono text-sm">
                        {payment.stripePaymentId || '-'}
                      </span>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
